import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const GARNET = '#5b1e22';
const COAL   = '#181617';
const ASHEN  = '#7A766F';
const BORDER = '#e5e2de';
const GREEN  = '#1a7a4c';
const BLUE   = '#1a5a7a';
const ORANGE = '#b35c00';
const RED    = '#b32d2d';

const TABS = [
  { key: 'overview',  label: 'Overview'        },
  { key: 'visits',    label: 'Visits'           },
  { key: 'source',    label: 'Paid vs Organic'  },
  { key: 'customers', label: 'Customers'        },
  { key: 'revenue',   label: 'Revenue'          },
  { key: 'piercings', label: 'Piercings'        },
];

// ─── Utilities ────────────────────────────────────────────────────────────────

function fmtHKD(v) {
  if (v == null) return '—';
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

function fmtFull(v) { return v == null ? '—' : `$${Math.round(v).toLocaleString()}`; }

function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(1)}%`; }

function fmtPeriod(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const yr = new Date().getFullYear();
  return d.getFullYear() !== yr
    ? `${months[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`
    : months[d.getMonth()];
}

function delta(current, prior) {
  if (!prior) return null;
  return Math.round((current - prior) / prior * 100);
}

// ─── Shared components ────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, delta: d, color }) {
  const up = d > 0, down = d < 0;
  return (
    <div style={{
      background: '#fff', borderRadius: 10, padding: '16px 20px',
      borderTop: `3px solid ${color || GARNET}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)', flex: 1, minWidth: 140,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: ASHEN, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: COAL, lineHeight: 1 }}>{value}</div>
      {(sub || d != null) && (
        <div style={{ fontSize: 11, color: ASHEN, marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
          {d != null && (
            <span style={{ fontWeight: 700, color: up ? GREEN : down ? RED : ASHEN }}>
              {up ? '▲' : down ? '▼' : '—'} {Math.abs(d)}% vs last month
            </span>
          )}
          {sub && !d && <span>{sub}</span>}
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, children, style }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', ...style }}>
      {title && <div style={{ fontSize: 13, fontWeight: 700, color: COAL, marginBottom: 16 }}>{title}</div>}
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, margin: '24px 0 10px' }}>
      {children}
    </div>
  );
}

function Loading() {
  return <div style={{ padding: 40, textAlign: 'center', color: ASHEN, fontSize: 13 }}>Loading…</div>;
}

function Empty({ text }) {
  return (
    <div style={{ padding: 40, textAlign: 'center', color: ASHEN }}>
      <div style={{ fontSize: 28, marginBottom: 8, color: '#ddd' }}>—</div>
      <div style={{ fontSize: 13 }}>{text || 'No data yet'}</div>
    </div>
  );
}

function SimpleBarTooltip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: COAL, marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ fontSize: 12, color: p.fill || p.color }}>
          {p.name}: {fmt ? fmt(p.value) : p.value}
        </div>
      ))}
    </div>
  );
}

function useData(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(url)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [url]);
  return { data, loading };
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────

function OverviewTab() {
  const { data: ov, loading: ovL } = useData('/dashboard/analytics/overview');
  const { data: funnel, loading: fnL } = useData('/dashboard/booking-funnel');

  if (ovL || fnL) return <Loading />;

  const d = ov || {};
  const f = funnel?.mtd || {};

  return (
    <div>
      <SectionLabel>This Month</SectionLabel>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <KpiCard label="Visits" value={d.visits_mtd ?? '—'} delta={delta(d.visits_mtd, d.visits_prior)} color={GARNET} />
        <KpiCard label="New Customers" value={d.new_customers_mtd ?? '—'} delta={delta(d.new_customers_mtd, d.new_customers_prior)} color={BLUE} />
        <KpiCard label="Revenue" value={fmtHKD(d.revenue_mtd)} delta={delta(d.revenue_mtd, d.revenue_prior)} color={GREEN} />
        <KpiCard label="Avg Revenue / Visit" value={fmtFull(d.avg_revenue_per_visit)} color={ORANGE} />
        <KpiCard label="Walk-ins" value={d.walk_ins_mtd ?? '—'} sub={d.visits_mtd ? `${Math.round(d.walk_ins_mtd / d.visits_mtd * 100)}% of visits` : undefined} color={ASHEN} />
        <KpiCard label="Under-18 Piercings" value={d.minor_piercings_mtd ?? '—'} color={PURPLE} />
        <KpiCard label="Avg Booking Lead Time" value={d.booking_lead_time_days != null ? `${d.booking_lead_time_days}d` : '—'} sub="days in advance" color={ASHEN} />
      </div>

      <SectionLabel>Booking Funnel — This Month</SectionLabel>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <KpiCard label="Booked" value={f.booked ?? '—'} color={BLUE} />
        <KpiCard label="Completed" value={f.completed ?? '—'} sub={f.booked ? `${f.show_rate}% show rate` : undefined} color={GREEN} />
        <KpiCard label="Cancelled" value={f.cancelled ?? '—'} sub={f.booked ? `${Math.round((f.cancelled || 0) / f.booked * 100)}% cancel rate` : undefined} color={RED} />
        <KpiCard label="Inferred No-Shows" value={f.no_show ?? '—'} color={ORANGE} />
        <KpiCard label="Walk-ins" value={f.walk_ins ?? '—'} sub="no prior booking" color={ASHEN} />
      </div>

      {funnel?.trend?.length > 0 && (
        <>
          <SectionLabel>Show Rate Trend</SectionLabel>
          <ChartCard>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={funnel.trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: ASHEN }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: ASHEN }} tickFormatter={v => `${v}%`} />
                <Tooltip formatter={v => `${v}%`} />
                <Line type="monotone" dataKey="show_rate" name="Show Rate" stroke={GREEN} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}
    </div>
  );
}

// ─── Tab: Visits ─────────────────────────────────────────────────────────────

function VisitsTab() {
  const { data: vb, loading: vbL } = useData('/dashboard/visit-breakdown');
  const { data: funnel, loading: fnL } = useData('/dashboard/booking-funnel');
  const { data: svc, loading: svcL } = useData('/dashboard/service-conversion');

  if (vbL || fnL || svcL) return <Loading />;

  const trend = vb?.trend || [];
  const fTrend = funnel?.trend || [];

  return (
    <div>
      <SectionLabel>Appointment Types — 12 Months</SectionLabel>
      {trend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} />
              <Tooltip content={<SimpleBarTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="new_piercing" name="New Piercing" stackId="a" fill={GARNET} />
              <Bar dataKey="downsizing" name="Downsizing" stackId="a" fill={BLUE} />
              <Bar dataKey="checkup" name="Check-up" stackId="a" fill={ORANGE} />
              <Bar dataKey="consultation" name="Consultation" stackId="a" fill={ASHEN} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>Walk-ins vs Booked — 12 Months</SectionLabel>
      {fTrend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={fTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} />
              <Tooltip content={<SimpleBarTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="completed" name="Booked & Completed" stackId="a" fill={GREEN} />
              <Bar dataKey="walk_ins" name="Walk-ins" stackId="a" fill={ORANGE} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>Booking Funnel — 12 Months</SectionLabel>
      {fTrend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={fTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} />
              <Tooltip content={<SimpleBarTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="booked" name="Booked" stackId="a" fill="#ccc" />
              <Bar dataKey="completed" name="Completed" stackId="b" fill={GREEN} />
              <Bar dataKey="cancelled" name="Cancelled" stackId="b" fill={RED} />
              <Bar dataKey="no_show" name="No-show" stackId="b" fill={ORANGE} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>Service Conversion — Free Visits → Paid</SectionLabel>
      {svc?.trend?.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={svc.trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: ASHEN }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={v => `${v}%`} />
              <Line type="monotone" dataKey="conversion_pct" name="Conversion Rate" stroke={GARNET} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty text="No service conversion data yet" />}
    </div>
  );
}

// ─── Tab: Paid vs Organic ─────────────────────────────────────────────────────

function SourceTab() {
  const { data, loading } = useData('/dashboard/analytics/paid-organic');

  if (loading) return <Loading />;
  if (!data) return <Empty />;

  const { trend = [], mtd = {}, sources_90d = [] } = data;

  return (
    <div>
      <SectionLabel>This Month</SectionLabel>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <KpiCard label="Paid Bookings" value={mtd.paid ?? '—'} sub="Google + Meta" color={BLUE} />
        <KpiCard label="Organic Bookings" value={mtd.organic ?? '—'} sub="website / manual" color={GREEN} />
        <KpiCard label="Paid Cancel Rate" value={fmtPct(mtd.paid_cancel_rate)} color={RED} />
        <KpiCard label="Organic Cancel Rate" value={fmtPct(mtd.organic_cancel_rate)} color={ORANGE} />
      </div>

      <SectionLabel>Paid vs Organic Volume — 12 Months</SectionLabel>
      {trend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} />
              <Tooltip content={<SimpleBarTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="paid" name="Paid" stackId="a" fill={BLUE} />
              <Bar dataKey="organic" name="Organic" stackId="a" fill={GREEN} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>Cancel Rate by Source — 12 Months</SectionLabel>
      {trend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={v => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="paid_cancel_rate" name="Paid" stroke={BLUE} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="organic_cancel_rate" name="Organic" stroke={GREEN} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>Inferred No-Shows — 12 Months</SectionLabel>
      {trend.some(t => t.inferred_noshows > 0) ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} allowDecimals={false} />
              <Tooltip content={<SimpleBarTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="paid_noshows" name="Paid" stackId="a" fill={BLUE} />
              <Bar dataKey="organic_noshows" name="Organic" stackId="a" fill={ORANGE} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: ASHEN, marginTop: 8 }}>Confirmed bookings with no check-in recorded (tablet era: Apr 2026+)</div>
        </ChartCard>
      ) : <Empty text="No inferred no-show data yet (requires tablet check-in era)" />}

      {sources_90d.length > 0 && (
        <>
          <SectionLabel>Booking Sources — Last 90 Days</SectionLabel>
          <ChartCard>
            {sources_90d.map(s => {
              const total = sources_90d.reduce((sum, x) => sum + x.count, 0);
              const pct = total > 0 ? Math.round(s.count / total * 100) : 0;
              return (
                <div key={s.source} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 90, fontSize: 12, fontWeight: 600, color: COAL, flexShrink: 0, textTransform: 'capitalize' }}>{s.source}</div>
                  <div style={{ flex: 1, background: '#f4f3f1', borderRadius: 4, height: 10, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: s.source === 'google' ? BLUE : s.source === 'meta' ? GARNET : GREEN, borderRadius: 4 }} />
                  </div>
                  <div style={{ width: 60, fontSize: 12, color: ASHEN, textAlign: 'right' }}>{s.count} ({pct}%)</div>
                </div>
              );
            })}
          </ChartCard>
        </>
      )}
    </div>
  );
}

// ─── Tab: Customers ───────────────────────────────────────────────────────────

function CustomersTab() {
  const { data: repeat, loading: rpL } = useData('/dashboard/repeat-rate');
  const { data: cv, loading: cvL } = useData('/dashboard/analytics/customer-value');

  if (rpL || cvL) return <Loading />;

  const repeatTrend = repeat?.trend || [];
  const returnTrend = cv?.return_rate_trend || [];
  const revShare = cv?.revenue_share_trend || [];

  return (
    <div>
      <SectionLabel>Repeat Customer Rate — 13 Months</SectionLabel>
      {repeatTrend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={repeatTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} />
              <Tooltip content={<SimpleBarTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="new_buyers" name="New" stackId="a" fill={BLUE} />
              <Bar dataKey="repeat_buyers" name="Repeat" stackId="a" fill={GARNET} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>New vs Repeat Revenue Share — 12 Months</SectionLabel>
      {revShare.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revShare} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} tickFormatter={fmtHKD} />
              <Tooltip content={<SimpleBarTooltip fmt={fmtHKD} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="new_revenue" name="New" stackId="a" fill={BLUE} />
              <Bar dataKey="repeat_revenue" name="Repeat" stackId="a" fill={GARNET} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>90-Day Return Rate by Cohort</SectionLabel>
      {returnTrend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={returnTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: ASHEN }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={v => `${v}%`} />
              <Line type="monotone" dataKey="return_rate" name="Return Rate" stroke={GARNET} strokeWidth={2} dot={{ r: 4, fill: GARNET }} />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: ASHEN, marginTop: 8 }}>% of new customers who made a 2nd purchase within 90 days of their first</div>
        </ChartCard>
      ) : <Empty text="Need at least 90 days of customer data" />}

    </div>
  );
}

// ─── Tab: Revenue ─────────────────────────────────────────────────────────────

function RevenueTab() {
  const { data: rev, loading: revL } = useData('/dashboard/revenue');
  const { data: rbd, loading: rbdL } = useData('/dashboard/revenue-breakdown');
  const { data: upsell, loading: upL } = useData('/dashboard/analytics/upsell-rate');

  if (revL || rbdL || upL) return <Loading />;

  const trend = rev?.trend || [];
  const breakdown = rbd?.breakdown || [];
  const upTrend = upsell?.trend || [];

  const totalBreakdown = breakdown.reduce((s, b) => s + (b.revenue || 0), 0);

  return (
    <div>
      <SectionLabel>Revenue Trend — 12 Months</SectionLabel>
      {trend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis tick={{ fontSize: 11, fill: ASHEN }} tickFormatter={fmtHKD} />
              <Tooltip content={<SimpleBarTooltip fmt={fmtHKD} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="piercing_revenue" name="Piercing" stackId="a" fill={GARNET} />
              <Bar dataKey="non_piercing_revenue" name="Other" stackId="a" fill={BLUE} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>Revenue Breakdown</SectionLabel>
      {breakdown.length > 0 ? (
        <ChartCard>
          {breakdown.map(b => {
            const pct = totalBreakdown > 0 ? Math.round(b.revenue / totalBreakdown * 100) : 0;
            return (
              <div key={b.category} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 150, fontSize: 12, fontWeight: 600, color: COAL, flexShrink: 0 }}>{b.category}</div>
                <div style={{ flex: 1, background: '#f4f3f1', borderRadius: 4, height: 12, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: GARNET, borderRadius: 4, opacity: 0.8 + pct * 0.002 }} />
                </div>
                <div style={{ width: 90, fontSize: 12, color: ASHEN, textAlign: 'right' }}>{fmtHKD(b.revenue)} ({pct}%)</div>
              </div>
            );
          })}
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>Upsell Rate — Additional Jewelry per Piercing Visit</SectionLabel>
      {upTrend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={upTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: ASHEN }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={(v, name) => name === 'upsell_rate' ? `${v}%` : v} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="upsell_rate" name="Upsell Rate" stroke={GREEN} strokeWidth={2} dot={{ r: 4, fill: GREEN }} />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: ASHEN, marginTop: 8 }}>% of piercing orders where more than 1 flatback per piercing was sold, or additional fashion jewelry was added</div>
        </ChartCard>
      ) : <Empty text="No upsell data yet" />}

      {rev?.aov && (
        <>
          <SectionLabel>Average Order Value</SectionLabel>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {Object.entries(rev.aov).map(([key, val]) => (
              <KpiCard key={key} label={`AOV ${key}`} value={fmtFull(val.aov)} sub={`${val.orders} orders`} color={GARNET} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tab: Piercings ───────────────────────────────────────────────────────────

function PiercingsTab() {
  const { data: minor, loading: minL } = useData('/dashboard/analytics/minor-stats');
  const { data: placement, loading: plL } = useData('/dashboard/placement-breakdown');
  const { data: dh, loading: dhL } = useData('/dashboard/downsizing-health');

  if (minL || plL || dhL) return <Loading />;

  const minorTrend = minor?.trend || [];
  const ageBreakdown = minor?.age_breakdown || [];
  const placements = placement?.placements || [];
  const dhSummary = dh?.summary || {};
  const dhTrend = dh?.trend || [];

  return (
    <div>
      <SectionLabel>Under-18 Piercings — 12 Months</SectionLabel>
      {minorTrend.length > 0 ? (
        <ChartCard>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={minorTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
              <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: ASHEN }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: ASHEN }} tickFormatter={v => `${v}%`} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="minor_piercings" name="Under-18 Piercings" fill={GARNET} radius={[3,3,0,0]} />
              <Line yAxisId="right" type="monotone" dataKey="pct_of_total" name="% of Total" stroke={BLUE} strokeWidth={2} dot={false} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : <Empty text="No under-18 piercing data yet" />}

      <SectionLabel>Placement Breakdown</SectionLabel>
      {placements.length > 0 ? (
        <ChartCard>
          {placements.map(pl => (
            <div key={pl.placement} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{ width: 110, fontSize: 12, fontWeight: 600, color: COAL, flexShrink: 0 }}>{pl.label}</div>
              <div style={{ flex: 1, background: '#f4f3f1', borderRadius: 4, height: 10, overflow: 'hidden' }}>
                <div style={{ width: `${pl.pct}%`, height: '100%', background: GARNET, borderRadius: 4 }} />
              </div>
              <div style={{ fontSize: 12, color: ASHEN, width: 90, textAlign: 'right' }}>
                {pl.count} ({pl.pct}%)
                <span style={{ marginLeft: 6, color: pl.trend === 'up' ? GREEN : pl.trend === 'down' ? RED : ASHEN, fontSize: 10 }}>
                  {pl.trend === 'up' ? '▲' : pl.trend === 'down' ? '▼' : ''}
                </span>
              </div>
            </div>
          ))}
        </ChartCard>
      ) : <Empty />}

      <SectionLabel>Downsizing Health</SectionLabel>
      {dhSummary.total > 0 ? (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <KpiCard label="Ready" value={dhSummary.ready ?? '—'} sub={`${dhSummary.ready_pct}% of downsizing visits`} color={GREEN} />
            <KpiCard label="Not Ready" value={dhSummary.not_ready ?? '—'} color={RED} />
            <KpiCard label="Not Needed" value={dhSummary.not_needed ?? '—'} color={ASHEN} />
          </div>
          {dhTrend.length > 0 && (
            <ChartCard>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dhTrend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                  <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: ASHEN }} />
                  <YAxis tick={{ fontSize: 11, fill: ASHEN }} />
                  <Tooltip content={<SimpleBarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="ready" name="Ready" stackId="a" fill={GREEN} />
                  <Bar dataKey="not_ready" name="Not Ready" stackId="a" fill={RED} />
                  <Bar dataKey="not_needed" name="Not Needed" stackId="a" fill={ASHEN} radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </>
      ) : <Empty text="No downsizing health data yet" />}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const PURPLE = '#6b4c8a';

export default function Analytics() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{
        background: '#fff', borderBottom: `1px solid ${BORDER}`,
        padding: '0 28px', display: 'flex', position: 'sticky', top: 0, zIndex: 10,
        overflowX: 'auto',
      }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 14, fontWeight: activeTab === tab.key ? 700 : 400,
            color: activeTab === tab.key ? GARNET : ASHEN,
            borderBottom: activeTab === tab.key ? `2px solid ${GARNET}` : '2px solid transparent',
            marginBottom: -1, whiteSpace: 'nowrap',
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '24px 28px' }}>
        {activeTab === 'overview'  && <OverviewTab />}
        {activeTab === 'visits'    && <VisitsTab />}
        {activeTab === 'source'    && <SourceTab />}
        {activeTab === 'customers' && <CustomersTab />}
        {activeTab === 'revenue'   && <RevenueTab />}
        {activeTab === 'piercings' && <PiercingsTab />}
      </div>
    </div>
  );
}
